import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FilePlus2, Check } from "lucide-react";
import PageHead from "@/components/PageHead";
import { toast } from "sonner";

const MODELS = ["Remote", "Hybrid", "Onsite", "Worldwide Remote"];

function Field({ label, ...props }) {
  return (
    <div>
      <label className="label-caps">{label}</label>
      <input {...props} className="mt-1.5 w-full rounded-xl border border-border bg-background px-3 py-3 text-sm outline-none focus:border-electric" />
    </div>
  );
}

export default function PostJob() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ role: "", location: "", salary: "", model: "Remote", desc: "", skills: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    toast.success(`"${form.role || "New role"}" posted — matching candidates now`);
    navigate("/recruiter/pipeline");
  };

  return (
    <div>
      <PageHead label="Post Job" title="Create a new opening"
        sub="Publish a role and CVOLVE instantly surfaces matched candidates." />

      <form onSubmit={submit} className="grid gap-6 lg:grid-cols-3">
        <div className="card-surface space-y-4 p-6 lg:col-span-2">
          <Field label="Role title" data-testid="job-role" value={form.role} onChange={(e) => set("role", e.target.value)} placeholder="Product Manager" required />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Location" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="London, UK" />
            <Field label="Salary" value={form.salary} onChange={(e) => set("salary", e.target.value)} placeholder="$135k" />
          </div>
          <div>
            <label className="label-caps">Work model</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {MODELS.map((m) => (
                <button type="button" key={m} onClick={() => set("model", m)}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${form.model === m ? "border-electric bg-electric text-white" : "border-border"}`}>{m}</button>
              ))}
            </div>
          </div>
          <Field label="Required skills (comma separated)" value={form.skills} onChange={(e) => set("skills", e.target.value)} placeholder="Roadmapping, SaaS, Analytics" />
          <div>
            <label className="label-caps">Description</label>
            <textarea rows={5} value={form.desc} onChange={(e) => set("desc", e.target.value)} placeholder="Describe the role…"
              className="mt-1.5 w-full resize-none rounded-xl border border-border bg-background p-3 text-sm outline-none focus:border-electric" />
          </div>
          <button type="submit" data-testid="post-job-submit" className="cv-btn-primary"><FilePlus2 className="h-4 w-4" /> Publish job</button>
        </div>

        <div className="card-surface h-fit p-6">
          <p className="label-caps mb-3">Live preview</p>
          <div className="rounded-2xl border border-border p-4">
            <p className="font-heading text-lg font-bold">{form.role || "Role title"}</p>
            <p className="text-sm text-muted-foreground">{form.location || "Location"}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="rounded-full bg-electric/10 px-2.5 py-1 text-xs font-semibold text-electric">{form.model}</span>
              {form.salary && <span className="rounded-full bg-lime/15 px-2.5 py-1 text-xs font-semibold text-lime">{form.salary}</span>}
            </div>
          </div>
          <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground"><Check className="h-4 w-4 text-lime" /> Auto-matched to your talent pool</p>
        </div>
      </form>
    </div>
  );
}
